import React, { FC } from 'react';
import i18next from 'i18next';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Image from "next/legacy/image";

import useMobileView from '@design-system/hooks/useMobileView';
import type { CarroAsIsProps } from '@source/components/CarroAsIs/CarroAsIsProps';
import useRegionConfig from '@source/hooks/useRegionConfig';
import { Divider } from 'antd';

const StyledWapper = styled.article<{ isListingDetail?: boolean; hasTitle?: boolean }>`
  margin: ${(props) => (props.isListingDetail ? '48px 0' : '26px 0')};

  .title {
    ${(props) => props.theme.typo.familyGoogle.medium};
    font-size: 14px;
    margin-bottom: 10px;
    color: #7a7a7b;
    line-height: auto;
  }

  @media screen and (min-width: 768px) {
    .title {
      font-size: 18px;
      margin-bottom: 20px;
      line-height: 22px;
    }
  }

  @media screen and (max-width: 767px) {
    margin: 10px 0;
  }
`;

const StyledWrapperContent = styled.section`
  max-width: 100% !important;
  border-radius: 12px;
  padding: 30px;
  height: auto;
  /* position: relative;
  cursor: pointer; */
  ${(props) => props.theme.typo.familyGoogle.regular}
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: #4e4e4f;
  box-shadow: 0px 6px 6px 0px rgba(161, 161, 161, 0.25);

  @media only screen and (min-width: 768px) {
    justify-content: center;
    min-height: 255px;
  }

  @media only screen and (min-width: 1280px) {
    height: 180px;
    min-height: unset;
  }
`;

const StyledDesktop = styled.section<{ lang?: string }>`
  width: 100%;
  color: #fff;

  .item-wrapper {
    height: 104px;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: pre-line;
    gap: 48px;
  }

  .items-list {
    display: flex;
    justify-content: space-between;
    flex-basis: 73%;
    align-items: center;
  }

  .item-text {
    color: #fff;
    max-height: 60px;
    font-size: 10.5px;
    font-weight: 400;
    margin-bottom: 0;
    line-height: 14px;
  }

  .item-title {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    /* cursor: pointer; */
  }

  .item {
    text-align: center;
    font-weight: 500;
    font-size: 14px;
    line-height: 140%;
  }

  .item-icon {
    margin-bottom: 12px;
  }

  .extra-info {
    ${(props) => props.theme.typo.familyGoogle.regular};
    font-size: 10.5px;
    line-height: 140%;
  }

  @media only screen and (min-width: 768px) {
    width: 503px;

    .item-wrapper {
      gap: 35px;
    }

    .extra-info {
      font-size: 10.5px;
      margin-top: 24px;
      text-align: center;
    }
  }

  @media only screen and (min-width: 1280px) {
    min-width: 767px;

    .item-icon {
      padding-top: 4px;
      margin-bottom: 14px;
    }

    .item-wrapper {
      gap: 40px;
    }

    .extra-info {
      margin-top: 0;
    }
  }
`;

const StyledTitle = styled.div`
  .sub-title {
    ${(props) => props.theme.typo.familyGoogle.light};
    color: #fff;
    line-height: 140%;
    font-size: 12px;
    margin-bottom: 0;
  }

  @media screen and (min-width: 768px) {
    .sub-title {
      font-size: 10.5px;
    }
  }
`;

const TitleText = styled.h2`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  color: #fff;
  font-size: 28px;
  line-height: 28px;
  margin-bottom: 24px;

  @media screen and (min-width: 768px) {
    min-width: 187px;
    font-size: 30px;
    margin-bottom: 14.5px;
  }

  @media screen and (min-width: 1280px) {
    min-width: 305px;
    font-size: 36px;
    line-height: 41px;
    margin-bottom: 24px;
  }
`;

const StyledMobile = styled.section`
  color: #fff;

  .item-title-text {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 24px;

    & > span:nth-child(2) {
      width: max-content;
    }
  }

  .item-wrapper {
    ${(props) => props.theme.typo.familyGoogle.medium}
    font-size: 12px;
    line-height: 140%;
    padding-inline-start: 0px;
    margin-bottom: 0;

    li {
      display: flex;
      & > div {
        padding-right: 12px;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }

    li:nth-child(even) {
      margin: 24px 0;

      @media screen and (min-width: 576px) {
        margin-top: 24px;
      }

      /* @media screen and (min-width: 768px) {
        margin-top: 24px;
      } */
    }
  }

  .extra-info {
    ${(props) => props.theme.typo.familyGoogle.regular}
    font-size: 12px;
    line-height: 140%;
  }
`;

const StyledLinkWrapper = styled.a`
  text-decoration: none;
`;

const StyledDivider = styled(Divider)`
  border-left-color: #fff;
  height: 104px;
`;

const CarroAsIs: FC<CarroAsIsProps> = ({ content, href, isListingDetail, hasTitle }) => {
  const isMobileScreenMaxWidth = useMobileView(768, true);
  const isLargeScreenMaxWidth = useMobileView(1280, true);
  const isMobileSmall = useMobileView(576, true);

  const { t } = useTranslation();

  const {
    countryConfig: { country },
  } = useRegionConfig();

  const { body, title, subTitle, extraInfo, sectionTitle, sectionTitleMobile } = content || {};

  const renderTitle = ({ titleText, subTitleText }: { titleText: string; subTitleText: string }) => (
    <StyledTitle>
      <TitleText>
        <Trans
          defaults={titleText}
          components={{
            span: <span />,
          }}
        />
      </TitleText>
      <p className="sub-title">
        <Trans defaults={subTitleText} components={{ span: <span style={{ color: '#FF4C00' }} />, br: <br /> }} />
      </p>
    </StyledTitle>
  );

  const renderDesktop = () => (
    <StyledDesktop lang={i18next.language}>
      {/* {isLargeScreenMaxWidth && (
        <div className="item-title">{renderTitle({ titleText: title, subTitleText: subTitle.desktop })}</div>
      )} */}
      <div className="item-wrapper">
        <div className="item-title">
          {renderTitle({ titleText: title, subTitleText: isLargeScreenMaxWidth ? subTitle.tablet : subTitle.desktop })}
        </div>
        {isLargeScreenMaxWidth && (
          <div role="presentation" aria-hidden="true">
            <StyledDivider type="vertical" />
          </div>
        )}
        <div className="items-list">
          {body?.map((item) => (
            <article className="item" key={item?.key}>
              <figure className="item-icon">
                <Image src={item?.icon?.desktop} alt={item?.text?.desktop} width={62} height={62} />
              </figure>
              <h3 className="item-text">
                <Trans defaults={item?.text?.desktop} components={{ br: <br /> }} />
              </h3>
            </article>
          ))}
          {!isLargeScreenMaxWidth && (
            <>
              <div role="presentation" aria-hidden="true">
                <StyledDivider type="vertical" />
              </div>
              <div className="extra-info">
                <Trans
                  defaults={extraInfo.desktop}
                  components={{ br: <br />, span: <span style={{ color: '#FF4C00' }} /> }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {isLargeScreenMaxWidth && (
        <div className="extra-info">
          <Trans defaults={extraInfo.tablet} components={{ br: <br />, span: <span style={{ color: '#FF4C00' }} /> }} />
        </div>
      )}
    </StyledDesktop>
  );

  const renderMobile = () => (
    <StyledMobile>
      <div className="item-title-text">
        {renderTitle({
          titleText: title,
          subTitleText: isMobileScreenMaxWidth && !isMobileSmall ? subTitle.mobile : subTitle.desktop,
        })}
      </div>
      <ul className="item-wrapper">
        {body.map((item) => (
          <li key={item?.key}>
            <div>
              <Trans defaults={item?.text?.mobile} components={{ br: <br /> }} />
            </div>
          </li>
        ))}
      </ul>
      <div className="extra-info">
        <Trans
          defaults={isMobileScreenMaxWidth && !isMobileSmall ? extraInfo.tablet : extraInfo.desktop}
          components={{ br: <br />, span: <span style={{ color: '#FF4C00' }} /> }}
        />
      </div>
    </StyledMobile>
  );

  return (
    <StyledWapper
      isListingDetail={isListingDetail}
      hasTitle={hasTitle}
      role="article"
      aria-label="Carro As Is Information"
    >
      {hasTitle && (
        <div className="title">
          <Trans
            defaults={isMobileScreenMaxWidth ? t(sectionTitleMobile) : t(sectionTitle)}
            components={{ br: <br />, span: <span style={{ color: '#4E4E4F' }} /> }}
          />
        </div>
      )}
      {/* <StyledLinkWrapper href={href}> */}
      <StyledWrapperContent>{isMobileScreenMaxWidth ? renderMobile() : renderDesktop()}</StyledWrapperContent>
      {/* </StyledLinkWrapper> */}
    </StyledWapper>
  );
};

export default CarroAsIs;
